import { useEffect, useRef, useState } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext.jsx";
import { openTrialCheckout, trialPricingPath } from "../utils/trialCheckout.js";

// An account from a free-trial button can use the app once Stripe Checkout has
// taken its card (plans.has_business_plan_access). Until then it is sent
// straight back to the Stripe form; the pricing page is the fallback when the
// form can't be opened.
export default function RequireBusinessPlan({ children }) {
  const { user, refreshUser } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [confirmedUserId, setConfirmedUserId] = useState(null);
  const [checkoutFailedUserId, setCheckoutFailedUserId] = useState(null);
  const askedUserIdRef = useRef(null);
  const checkoutUserIdRef = useRef(null);
  const pricingPathRef = useRef("");
  const userId = user?.id ?? null;
  const blocked = user?.business_access === false;
  const confirmedBlocked = blocked && confirmedUserId === userId;

  useEffect(() => {
    // The saved copy of the account can be from before its plan was paid for
    // (paid in another tab, or the tab closed before the plan was confirmed).
    // Ask the server once before sending a paying customer back to Stripe.
    if (!blocked || confirmedUserId === userId || askedUserIdRef.current === userId) return;
    askedUserIdRef.current = userId;
    Promise.resolve(refreshUser?.())
      .catch(() => null)
      .finally(() => setConfirmedUserId(userId));
  }, [blocked, confirmedUserId, refreshUser, userId]);

  useEffect(() => {
    if (!confirmedBlocked || checkoutUserIdRef.current === userId) return;
    checkoutUserIdRef.current = userId;
    const returnTo = `${location.pathname}${location.search}`;
    const pricingPath = trialPricingPath(returnTo);
    pricingPathRef.current = pricingPath;
    Promise.resolve()
      .then(() => openTrialCheckout({ interval: "monthly", returnTo, pricingPath }))
      .then((opened) => {
        if (!opened) setCheckoutFailedUserId(userId);
      })
      .catch(() => setCheckoutFailedUserId(userId));
    // Opened once per account; the address it returns to is the one shown now.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [confirmedBlocked, userId]);

  useEffect(() => {
    // Back from Stripe can bring this page back as it was when it left. Its
    // address is the pricing page by then: show that page.
    const onPageShow = (event) => {
      if (event.persisted && pricingPathRef.current) navigate(pricingPathRef.current, { replace: true });
    };
    window.addEventListener("pageshow", onPageShow);
    return () => window.removeEventListener("pageshow", onPageShow);
  }, [navigate]);

  if (blocked) {
    if (confirmedUserId !== userId || checkoutFailedUserId !== userId) {
      return <div className="authGate">Checking credentials…</div>;
    }
    return <Navigate to="/for-businesses/pricing" replace />;
  }
  return children;
}
